import { useEffect } from 'react';

import { useShallow } from 'zustand/react/shallow';

import { publishRecordingActive } from '@/shared/lib/cross-tab-recording-signal';

import { useMicStore } from './mic-store';
import { useRecordingStore } from './recording-store';
import { useDisplayMediaControls } from './use-display-media-controls';
import { useMic } from './use-mic';

export const useRecording = (
    clientId: string,
    shortLengthThreshold?: number,
    longLengthThreshold?: number,
) => {
    useMic();

    const { _initialize, _pause, _stop, _reset, status, audioRecorder, length } = useRecordingStore(
        useShallow((state) => ({
            _initialize: state._initialize,
            _pause: state._pause,
            _stop: state._stop,
            _reset: state._reset,
            status: state.status,
            audioRecorder: state.audioRecorder,
            length: state.length,
        })),
    );

    const { clear: clearDisplayMedia } = useDisplayMediaControls();

    const micStream = useMicStore((s) => s.currentStream);

    // Keeps the live recorder pointed at the current mic. Reacting to the store rather than
    // checking inside start() is what makes a swap or disconnect mid-start safe: the recorder
    // lands here the moment _start() commits it, whatever happened while start() was awaiting.
    useEffect(() => {
        if (!audioRecorder) {
            return;
        }

        if (micStream) {
            // connectStream() no-ops when disposed, so a rejection here is a real failure — log it.
            audioRecorder.connectStream('mic', micStream).catch((error) => {
                console.error('Error reconnecting mic stream:', error);
            });
        } else if (_pause()) {
            // Nothing to capture from — hold the recording rather than run it against a dead
            // track. Resuming once a mic returns is the clinician's call.
            audioRecorder.pause();
        }
    }, [micStream, audioRecorder, _pause]);

    useEffect(() => {
        _initialize(clientId, shortLengthThreshold, longLengthThreshold);
        return () => {
            _reset();
            clearDisplayMedia();
        };
    }, [
        clientId,
        shortLengthThreshold,
        longLengthThreshold,
        _initialize,
        _reset,
        clearDisplayMedia,
    ]);

    useEffect(() => {
        if (length === 'too-long' && _stop()) {
            audioRecorder?.pause();
        }
    }, [length, audioRecorder, _stop]);

    const isRecording = status === 'recording';
    useEffect(() => {
        if (isRecording) {
            return publishRecordingActive();
        }
    }, [isRecording]);
};
